import React, { useState } from "react";
import stilePulsanti from "../../../interfaccia/stilePulsanti";
import KitModaModal from "./KitModaModal";
import RicercaArticoloKit from "./RicercaArticoloKit";
import { messaggioErrore } from "./kitModaApi";

const QUANTITA_MASSIMA = 999;

const quantitaValida = (value) => {
  const numero = Number(value);
  return Number.isInteger(numero) && numero >= 1 && numero <= QUANTITA_MASSIMA;
};

/**
 * Editor della composizione dell'articolo (solo Domino): elenco di articoli con
 * quantita', "Aggiungi articolo" apre la ricerca e poi la quantita'.
 */
function EditorComposizione({ codVS, api, iniziale, onClose, onSaved }) {
  const [righe, setRighe] = useState(iniziale);
  const [passo, setPasso] = useState("elenco");
  const [nuovo, setNuovo] = useState(null);
  const [quantitaNuovo, setQuantitaNuovo] = useState("1");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const valida = righe.every((riga) => quantitaValida(riga.quantita));

  const salva = async (componenti) => {
    setSaving(true);
    setError("");
    try {
      const stato = await api.salvaComposizione(
        codVS,
        componenti.map((riga) => ({ codVS: riga.codVS, quantita: Number(riga.quantita) })),
      );
      onSaved(stato);
    } catch (err) {
      setError(messaggioErrore(err, "Salvataggio della composizione non riuscito"));
      setSaving(false);
    }
  };

  const footer =
    passo === "elenco" ? (
      <div className="flex flex-wrap justify-between items-center">
        <button
          type="button"
          className={stilePulsanti.buttonClassesWhite}
          onClick={() => setPasso("articolo")}
          disabled={saving}
        >
          Aggiungi articolo
        </button>
        <div>
          <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={onClose} disabled={saving}>
            Annulla
          </button>
          <button
            type="button"
            className={stilePulsanti.buttonClassesGreen}
            onClick={() => salva(righe)}
            disabled={saving || !valida || (righe.length === 0 && iniziale.length === 0)}
          >
            {saving ? "Salvo..." : righe.length ? "Salva composizione" : "Elimina composizione"}
          </button>
        </div>
      </div>
    ) : passo === "articolo" ? (
      <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={() => setPasso("elenco")}>
        Indietro
      </button>
    ) : (
      <div className="flex justify-between">
        <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={() => setPasso("articolo")}>
          Indietro
        </button>
        <button
          type="button"
          className={stilePulsanti.buttonClassesGreen}
          disabled={!quantitaValida(quantitaNuovo)}
          onClick={() => {
            setRighe((attuali) => [
              ...attuali,
              { codVS: nuovo.codVS, nome: nuovo.nome, codProd: nuovo.codProd, quantita: Number(quantitaNuovo) },
            ]);
            setNuovo(null);
            setPasso("elenco");
          }}
        >
          Aggiungi alla composizione
        </button>
      </div>
    );

  return (
    <KitModaModal
      title={iniziale.length ? "Modifica composizione" : "Rendi l'articolo un bundle"}
      subtitle={`Di quali articoli è fatto ${codVS}, e quanti pezzi di ciascuno. È la proposta di partenza per le singole taglie: ogni taglia può poi avere un kit diverso.`}
      onClose={onClose}
      busy={saving}
      footer={footer}
    >
      {error && (
        <p className="mt0 dark-red" role="alert">
          {error}
        </p>
      )}
      {passo === "elenco" &&
        (righe.length === 0 ? (
          <p className="mt0">Nessun articolo nella composizione. Aggiungine uno.</p>
        ) : (
          <table className="collapse w-100" aria-label="Articoli della composizione">
            <thead>
              <tr className="tl bb bordoColAlt">
                <th className="pv2 pr2">Articolo</th>
                <th className="pv2 pr2">Quantità</th>
                <th className="pv2" aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {righe.map((riga, index) => (
                <tr key={riga.codVS} className="bb bordoColAlt">
                  <td className="pv2 pr2">
                    <span className="b">{riga.codVS}</span> {riga.nome || ""}
                  </td>
                  <td className="pv2 pr2">
                    <input
                      type="number"
                      min="1"
                      max={QUANTITA_MASSIMA}
                      step="1"
                      className="ba bordoColScrittaDef-10 pa1 br2 w4"
                      value={riga.quantita}
                      aria-label={`Quantità di ${riga.codVS}`}
                      onChange={(event) =>
                        setRighe((attuali) =>
                          attuali.map((item, i) => (i === index ? { ...item, quantita: event.target.value } : item)),
                        )
                      }
                    />
                  </td>
                  <td className="pv2 tr">
                    <button
                      type="button"
                      className={stilePulsanti.buttonClassesWhite}
                      onClick={() => setRighe((attuali) => attuali.filter((_, i) => i !== index))}
                      aria-label={`Togli ${riga.codVS}`}
                    >
                      Togli
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      {passo === "articolo" && (
        <RicercaArticoloKit
          api={api}
          escludi={[codVS, ...righe.map((riga) => riga.codVS)]}
          onSelect={(articolo) => {
            setNuovo(articolo);
            setQuantitaNuovo("1");
            setPasso("quantita");
          }}
        />
      )}
      {passo === "quantita" && nuovo && (
        <div>
          <p className="mt0">
            <span className="b">{nuovo.codVS}</span> {nuovo.nome}
          </p>
          <label className="db mb2 b" htmlFor="kitModaQuantitaComposizione">
            Quanti pezzi servono per un {codVS}?
          </label>
          <input
            id="kitModaQuantitaComposizione"
            type="number"
            min="1"
            max={QUANTITA_MASSIMA}
            step="1"
            className="ba bordoColScrittaDef-10 pa2 br2 w4"
            value={quantitaNuovo}
            onChange={(event) => setQuantitaNuovo(event.target.value)}
            autoFocus
          />
        </div>
      )}
    </KitModaModal>
  );
}

/** Riquadro in alto: la composizione dell'articolo, o quella ricavata dai kit gia' presenti. */
function ComposizioneArticolo({ codVS, stato, api, puoModificare, onStato }) {
  const [editor, setEditor] = useState(null);
  const schema = stato?.schema ?? [];
  const proposto = stato?.schemaProposto ?? null;

  const apri = (righe) =>
    setEditor(
      righe.map((riga) => ({
        codVS: riga.componenteCodVS,
        nome: riga.nome,
        codProd: riga.codProd ?? null,
        quantita: riga.quantita,
      })),
    );

  return (
    <div className="kitModaComposizione pa3 br2 ba bordoColAlt mb3">
      <div className="flex flex-wrap items-start justify-between">
        <div className="mr3 mb2">
          <h4 className="mt0 mb1">Composizione dell'articolo</h4>
          <p className="mt0 mb0 f6 scrittaColMedio">
            Vale per tutte le taglie come proposta e resta in Domino: le singole taglie possono avere un kit diverso.
          </p>
        </div>
        {puoModificare && (
          <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={() => apri(schema)}>
            {schema.length ? "Modifica composizione" : "Rendi bundle"}
          </button>
        )}
      </div>

      {schema.length > 0 ? (
        <ul className="list pl0 mv2" aria-label="Composizione dell'articolo">
          {schema.map((riga) => (
            <li key={riga.componenteCodVS} className="pv1">
              <span className="b">{riga.quantita} ×</span> {riga.componenteCodVS}
              {riga.nome ? ` · ${riga.nome}` : ""}
              {riga.codProd ? <span className="scrittaColMedio"> · {riga.codProd}</span> : null}
            </li>
          ))}
        </ul>
      ) : proposto ? (
        <div className="mt2">
          <p className="mt0 mb1 f6">I kit già presenti sulle taglie suggeriscono questa composizione:</p>
          <ul className="list pl0 mv1" aria-label="Composizione suggerita">
            {proposto.map((riga) => (
              <li key={riga.componenteCodVS} className="pv1">
                <span className="b">{riga.quantita} ×</span> {riga.componenteCodVS}
                {riga.nome ? ` · ${riga.nome}` : ""}
                {riga.quantitaDiverse && (
                  <span className="db f7 dark-red">Quantità diverse fra le taglie: controlla prima di usarla.</span>
                )}
              </li>
            ))}
          </ul>
          {puoModificare && (
            <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={() => apri(proposto)}>
              Usa come composizione
            </button>
          )}
        </div>
      ) : (
        <p className="mt2 mb0 f6">Nessuna composizione: l'articolo non è ancora un bundle.</p>
      )}

      {editor && (
        <EditorComposizione
          codVS={codVS}
          api={api}
          iniziale={editor}
          onClose={() => setEditor(null)}
          onSaved={(nuovoStato) => {
            setEditor(null);
            onStato(nuovoStato);
          }}
        />
      )}
    </div>
  );
}

export default ComposizioneArticolo;
